import { CatalogApi } from '@backstage/catalog-client';
import { Entity, UserEntity } from '@backstage/catalog-model';

import { isLocalDevelopment } from './isLocalDevelopment';

/**
 * Only the parts a display name can come from. User entities carry a large
 * annotation set from the msgraph ingestion, and a name lookup has no use for
 * any of it.
 */
const DISPLAY_NAME_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'spec.profile.displayName',
];

/**
 * Refs per batched request. The catalog takes the whole list in one POST, so
 * this only bounds the response size.
 */
const BATCH_SIZE = 100;

const formatUserRefFallback = (userRef: string): string => {
  const match = userRef.match(/^user:[^/]+\/(.+)$/i);
  const username = match?.[1] ? decodeURIComponent(match[1]) : userRef;
  const namePart = username.replace(/_tomtom\.com$/, '');

  return namePart
    .split('.')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const displayNameFor = (userRef: string, entity?: Entity): string => {
  if (entity) {
    return (
      (entity as UserEntity).spec?.profile?.displayName ??
      (entity as UserEntity).metadata.title ??
      (entity as UserEntity).metadata.name
    );
  }
  return isLocalDevelopment() ? formatUserRefFallback(userRef) : userRef;
};

export const fetchUserName = async (
  userRef: string | undefined,
  catalogApi: CatalogApi,
) => {
  if (!userRef) return '';
  const entity = await catalogApi.getEntityByRef(userRef);
  return displayNameFor(userRef, entity);
};

/**
 * Resolves many refs through batched catalog requests instead of one request
 * per ref.
 *
 * Resolving names one at a time meant a screen listing N ideas opened N (plus
 * one per member) concurrent catalog requests. The dev server speaks HTTP/1.1,
 * where the browser caps itself at six connections per origin and queues the
 * rest, so it only ever looked slow. Over HTTP/2 in front of the real gateway
 * the same burst becomes hundreds of simultaneous streams, and the ones past
 * the server's concurrency limit are reset — surfacing as
 * ERR_HTTP2_PROTOCOL_ERROR and a "Failed to fetch" alert.
 *
 * Duplicate refs collapse, so a person on several ideas is fetched once.
 */
export const fetchUserNames = async (
  userRefs: Iterable<string | undefined>,
  catalogApi: CatalogApi,
): Promise<Map<string, string>> => {
  const refs = Array.from(
    new Set(Array.from(userRefs).filter((ref): ref is string => Boolean(ref))),
  );
  const names = new Map<string, string>();

  for (let start = 0; start < refs.length; start += BATCH_SIZE) {
    const chunk = refs.slice(start, start + BATCH_SIZE);
    let items: Array<Entity | undefined> = [];

    try {
      ({ items } = await catalogApi.getEntitiesByRefs({
        entityRefs: chunk,
        fields: DISPLAY_NAME_FIELDS,
      }));
    } catch {
      // A batch fails as a unit, so fall back to the ref-derived name for the
      // whole chunk rather than losing every name on the screen.
      items = [];
    }

    chunk.forEach((ref, index) => {
      names.set(ref, displayNameFor(ref, items[index]));
    });
  }

  return names;
};
